import { Suspense, useCallback, useMemo, useRef, useState } from "react";
import { editorComponents, editorDef } from "./CustomCtrl";
import { useTranslation } from "react-i18next";
import CustomForm from "./CustomForm";
import { deepClone, deepMerge } from "../utils/objectUtilities";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import { useAsyncEffect } from "../hooks/asyncEffect";
import ReactForm from "./ReactForm";
import Select from "./Select";

/**
 * Translation hook that resolves control type names across customCtrl → customCtrlCommon → default namespaces
 */
export const useCtrlTranslation = () => {
    const { t: tSpecific } = useTranslation('customCtrl');
    const { t: tCommon } = useTranslation('customCtrlCommon');
    const { t: tGeneric } = useTranslation();
    return useCallback(key => {
        const specific = tSpecific(key);
        if (specific === key) {
            const common = tCommon(key);
            if (common === key) {
                return tGeneric(key);
            }
            return common;
        }
        return specific;
    }, [tSpecific, tCommon, tGeneric]);
}

/**
 * A dropdown that uses the control registry to show a list of available custom control types
 * @component
 */
export const CustomCtrlTypeDropdown = ({ ...props }) => {
    const tDef = useCtrlTranslation();
    const customCtrlDefs = useMemo(() => editorComponents(tDef), [tDef]);
    return <Select options={customCtrlDefs} labelField="label" valueField="kind" {...props}></Select>
}

// the core fields used to set where custom control data should be stored
const fieldFields = t => [[
    { type: "text", field: "field", label: t('field_one'), required: true }
]]

// the common fields used to govern the operation of a control
const coreCommonFields = (t, { editable }) => {
    const columns = [];
    if (editable !== false) {
        columns.push([
            { type: "checkbox", field: "required", label: t('required') },
            { type: "checkbox", field: "readonly", label: t('readonly') },
            { type: "checkbox", field: "disabled", label: t('disabled') }
        ])
    }
    return columns;
}

// adds form specific common fields
const formCommonFields = (t, { editable } = {}) => {
    const columns = coreCommonFields(t, { editable });
    columns.push([
        { type: "checkbox", field: "hideLabel", label: t('hideLabel')},
        { type: "number", min: 1, max: 12, step: 1, field: "colspan", label: t('colspan'), required: true }
    ])
    return columns
}

// adds table specific common fields
const tableCommonFields = (t, { editable } = {}) => {
    return coreCommonFields(t, { editable });
}

const CustomCtrlDefFactory = ({ tools, kind, value, onChange }) => {
    const { t } = useTranslation();
    const [kindDef, setKindDef] = useState();
    useAsyncEffect(
        () => kind && editorDef(t, kind, tools),
        (kindDef) => setKindDef(kindDef),
        [t, kind]
    );

    if (kindDef?.control) {
        const Control = kindDef?.control;
        return <Control value={value} tools={tools} restricted={false} disabled={false} required={true} hideLabel={false} readonly={false} onChange={onChange}></Control>
    } else if (kindDef?.form) {
        return <CustomForm def={kindDef.form} live={true} data={value} restricted={false} disabled={false} required={true} hideLabel={false} readonly={false} onChange={onChange}></CustomForm>
    }
}

const CustomCtrlDefModalFactory = ({ Ctrl, showModal, kind, value, onClose }) => {
    const [cell, setCell] = useState(deepClone(value));
    const formRef = useRef();

    const handleCellUpdate = useCallback((delta) => {
        setCell(cell => deepMerge({}, cell, delta));
    }, []);

    const handleSave = useCallback(() => {
        if (formRef.current?.checkValidity()) {
            onClose(cell);
        }
    }, [onClose, cell])

    return <ReactNodeDialog showModal={showModal} size="xlg" onSave={handleSave} onClose={() => onClose(false)}>
        <ReactForm ref={formRef}>
            <Suspense>
                <Ctrl kind={kind} value={cell} onChange={handleCellUpdate}></Ctrl>
            </Suspense>
        </ReactForm>
    </ReactNodeDialog>
}

/**
 * A custom control defition in the context of a form
 * @component
 */
export const CustomCtrlDef = ({ ...props }) => {
    const tools = useMemo(() => ({
        labelFields: (t, { searchable } = {}) => {
            const labels = [[
                { type: "text", field: "label", label: t('label') }
            ]];
            if (searchable !== false) {
                labels.push([{ type: "text", field: "searchLabel", label: t('searchLabel') }])
            }
            return labels;
        },
        fieldFields,
        commonFields: formCommonFields
    }), [])
    return <CustomCtrlDefFactory tools={tools} {...props}></CustomCtrlDefFactory>
}

/**
 * A custom control definition in a modal in the context of a form
 * @component
 */
export const CustomCtrlDefModal = ({ ...props }) => <CustomCtrlDefModalFactory Ctrl={CustomCtrlDef} {...props}></CustomCtrlDefModalFactory>

/**
 * A custom control definition in the context of a table column
 * @component
 */
export const CustomColumnDef = ({ ...props }) => {
    const tools = useMemo(() => ({
        labelFields: t => {
            return [[
                { type: "text", field: "title", label: t('title') },
                { type: "text", field: "label", label: t('label') }
            ]]
        },
        fieldFields,
        commonFields: tableCommonFields
    }), [])
    return <CustomCtrlDefFactory tools={tools} {...props}></CustomCtrlDefFactory>
}

/**
 * A custom control definition in a model in the context of a table
 * @component
 */
export const CustomColumnDefModal = ({ ...props }) => <CustomCtrlDefModalFactory Ctrl={CustomColumnDef} {...props}></CustomCtrlDefModalFactory>